/**
 * Weather Icon Mapping Utility
 * Maps WMO weather codes to icon names
 */

import i18n from '../i18n';
import { WeatherCode, WeatherIconName } from '../types/weather.types';

// Mapping of WMO weather codes to icon names
const weatherIconMap: Record<WeatherCode, WeatherIconName> = {
  0: 'clear',           // Clear sky
  1: 'mostly-clear',    // Mainly clear
  2: 'partly-cloudy',   // Partly cloudy
  3: 'cloudy',          // Overcast
  45: 'fog',            // Fog
  48: 'fog',            // Depositing rime fog
  51: 'drizzle',        // Light drizzle
  53: 'drizzle',        // Moderate drizzle
  55: 'drizzle',        // Dense drizzle
  61: 'rain',           // Slight rain
  63: 'rain',           // Moderate rain
  65: 'heavy-rain',     // Heavy rain
  71: 'snow',           // Slight snow
  73: 'snow',           // Moderate snow
  75: 'snow',           // Heavy snow
  77: 'snow',           // Snow grains
  80: 'showers',        // Slight rain showers
  81: 'showers',        // Moderate rain showers
  82: 'showers',        // Violent rain showers
  85: 'snow',           // Slight snow showers
  86: 'snow',           // Heavy snow showers
  95: 'thunderstorm',   // Thunderstorm
  96: 'thunderstorm',   // Thunderstorm with slight hail
  99: 'thunderstorm',   // Thunderstorm with heavy hail
};

/**
 * Get weather icon name from WMO weather code
 * @param code - WMO weather code
 * @returns Icon name string for your icon library
 */
export function getWeatherIcon(code: WeatherCode): WeatherIconName {
  return weatherIconMap[code] || 'unknown';
}

/**
 * Get emoji representation of weather (for fallback/debugging)
 * @param code - WMO weather code
 * @returns Weather emoji
 */
export function getWeatherEmoji(code: WeatherCode): string {
  const emojiMap: Record<WeatherCode, string> = {
    0: '☀️',   // Clear
    1: '🌤️',   // Mostly clear
    2: '⛅',   // Partly cloudy
    3: '☁️',   // Cloudy
    45: '🌫️', // Fog
    48: '🌫️', // Fog
    51: '🌧️', // Drizzle
    53: '🌧️', // Drizzle
    55: '🌧️', // Drizzle
    61: '🌧️', // Rain
    63: '🌧️', // Rain
    65: '🌧️', // Heavy rain
    71: '❄️',  // Snow
    73: '❄️',  // Snow
    75: '❄️',  // Snow
    77: '❄️',  // Snow
    80: '🌦️', // Showers
    81: '🌦️', // Showers
    82: '🌦️', // Showers
    85: '❄️',  // Snow
    86: '❄️',  // Snow
    95: '⛈️',  // Thunderstorm
    96: '⛈️',  // Thunderstorm
    99: '⛈️',  // Thunderstorm
  };
  
  return emojiMap[code] || '🌡️';
}

/**
 * Get human-readable weather description using i18n translations
 * @param code - WMO weather code
 * @returns Translated weather description string
 */
export function getWeatherDescription(code: WeatherCode): string {
  const conditionKeyMap: Record<WeatherCode, string> = {
    0: 'clearSky',
    1: 'mainlyClear',
    2: 'partlyCloudy',
    3: 'overcast',
    45: 'foggy',
    48: 'foggy',
    51: 'lightDrizzle',
    53: 'moderateDrizzle',
    55: 'denseDrizzle',
    61: 'slightRain',
    63: 'moderateRain',
    65: 'heavyRain',
    71: 'slightSnow',
    73: 'moderateSnow',
    75: 'heavySnow',
    77: 'snowGrains',
    80: 'lightRainShowers',
    81: 'moderateRainShowers',
    82: 'heavyRainShowers',
    85: 'lightSnowShowers',
    86: 'heavySnowShowers',
    95: 'thunderstorm',
    96: 'thunderstormWithHail',
    99: 'thunderstormWithHeavyHail',
  };

  const key = conditionKeyMap[code] || 'unknown';
  return i18n.t(`weather.conditions.${key}`);
}
